/**
 * The theme swap, as a circular reveal from wherever the user clicked.
 *
 * Deliberately not in `theme.ts`. That module is the *contract* the generated
 * stylesheet reads — three attribute states and nothing else — and it is guarded
 * by a test that reads its source. Animation is a second concern with a second
 * failure mode, so it gets a second file and `theme.ts` stays readable in one
 * screen.
 *
 * Three ways this declines to animate, and all three still swap the theme:
 *
 *   - **No View Transitions API.** The swap happens; the page just changes.
 *   - **`prefers-reduced-motion: reduce`.** Checked here in script rather than
 *     left to the `!important` block in `base.css`, because that block only
 *     reaches CSS animations. A `WAAPI` animation ignores it entirely, so a
 *     full-viewport wipe would be exactly the motion that block exists to
 *     suppress.
 *   - **No origin.** The OS changing `prefers-color-scheme` under a `system`
 *     preference has no point on screen to grow from, and a wipe from the
 *     corner of a window nobody touched reads as a glitch.
 *
 * The duration and easing are read off the motion tokens rather than written
 * here, so the one rule in DESIGN.md section 1 holds in script as well as in
 * CSS: this file names no value of its own.
 */

/** The point the reveal grows from — normally the center of the clicked control. */
export interface RevealOrigin {
  readonly x: number
  readonly y: number
}

function prefersReducedMotion (): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/** A `--motion-*` token as milliseconds. Falls back to the browser default. */
function motionToken (name: string, fallback: number): number {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  const value = Number.parseFloat(raw)
  if (!Number.isFinite(value)) return fallback
  return raw.endsWith('ms') ? value : value * 1000
}

/**
 * The radius that reaches the furthest corner from the origin, so the reveal
 * finishes by covering the viewport rather than by stopping short of a corner.
 */
function coveringRadius (origin: RevealOrigin): number {
  return Math.hypot(
    Math.max(origin.x, window.innerWidth - origin.x),
    Math.max(origin.y, window.innerHeight - origin.y)
  )
}

/**
 * Runs `swap` — which must be what actually changes the theme — inside a view
 * transition, then grows the new theme over the old one from `origin`.
 *
 * `swap` is awaited on every path, including every path that does not animate,
 * so a caller never has to handle "did the theme change or not".
 */
export async function revealThemeChange (
  swap: () => void | Promise<void>,
  origin: RevealOrigin | null
): Promise<void> {
  // The DOM library types this as always present; Firefox before 144 and every
  // Safari before 18 disagree, so the guard is a runtime one.
  const start =
    typeof document.startViewTransition === 'function' ? document.startViewTransition.bind(document) : null

  if (!start || !origin || prefersReducedMotion()) {
    await swap()
    return
  }

  const transition = start(swap)
  try {
    await transition.ready
  } catch {
    // The browser skipped the transition — a second one started, or the
    // document was hidden. The swap inside it still ran, so there is nothing to
    // undo and nothing to animate.
    return
  }

  const radius = coveringRadius(origin)
  document.documentElement.animate(
    {
      clipPath: [
        `circle(0px at ${origin.x}px ${origin.y}px)`,
        `circle(${radius}px at ${origin.x}px ${origin.y}px)`
      ]
    },
    {
      duration: motionToken('--motion-slow', 280),
      easing: getComputedStyle(document.documentElement).getPropertyValue('--motion-ease').trim() || 'ease',
      // The incoming theme is the thing being revealed; the outgoing one is a
      // still image underneath it. Animating both would cross-fade the wipe
      // into mush.
      pseudoElement: '::view-transition-new(root)'
    }
  )
}
